import React, { useCallback, useEffect, useState } from "react";
import styles from "./InsuranceSync.module.css";
import Footer from "../../components/ui/Footer";
import Navbar from "../../components/ui/Navbar";
import Menu from "../../components/ui/Menu";
import InsuranceSyncService from "../../utils/api/InsuranceSyncService";
import FixedAgent from "../../utils/api/FixedAgent";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "../../components/list/Pagination";
import { toClientPaginatedResult } from "../../utils/pagination";

function InsuranceSync(props) {
  const [pendingRows, setPendingRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = useSelector((state) => state.userData.loginData.token);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [submittingRow, setSubmittingRow] = useState(null);
  const [expiryDates, setExpiryDates] = useState({});
  const [copiedRow, setCopiedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const ROWS_PER_PAGE = 10;

  // Derived: apply search + date filter
  const filteredRows = pendingRows.filter((row) => {
    const matchesSearch = searchQuery
      ? String(row.plateNumber || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      : true;
    const matchesDate = filterDate
      ? String(row.timestamp || "").startsWith(filterDate)
      : true;
    return matchesSearch && matchesDate;
  });

  const hasActiveFilters = searchQuery || filterDate;
  // This screen is backed by a spreadsheet rather than a paginated endpoint.
  // Its filters must run before a deliberately client-side page is selected.
  const clientPage = toClientPaginatedResult({
    items: filteredRows,
    page: currentPage,
    perPage: ROWS_PER_PAGE,
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterDate("");
    setCurrentPage(1);
  };

  const handleCopyPlate = (plateNumber, rowNumber) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(plateNumber);
    } else {
      // Fallback for non-HTTPS (e.g. mobile over LAN)
      const textarea = document.createElement("textarea");
      textarea.value = plateNumber;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopiedRow(rowNumber);
    setTimeout(() => setCopiedRow(null), 2000);
  };

  const closeMenuHandler = () => {
    props.setShowMenu(false);
  };

  /**
   * Load and sync data on mount
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setSyncMessage("");

      const rows = await InsuranceSyncService.initSync();
      setPendingRows(rows);

      if (rows.length === 0) {
        setSyncMessage("All entries have been processed.");
      } else {
        setSyncMessage(
          `${rows.length} pending ${rows.length === 1 ? "entry" : "entries"} found.`,
        );
      }
    } catch (err) {
      console.error("Sync error:", err);
      toast.error(err.message || "Failed to sync insurance data", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      setSyncMessage("Failed to sync data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Handle expiry date input change for a specific row
   */
  const handleExpiryChange = (rowNumber, value) => {
    setExpiryDates((prev) => ({
      ...prev,
      [rowNumber]: value,
    }));
  };

  /**
   * Submit expiry date for a row — marks it as "successful"
   */
  const handleSubmit = async (rowNumber) => {
    const expiryDate = expiryDates[rowNumber];
    const row = pendingRows.find((r) => r.rowNumber === rowNumber);

    if (!expiryDate) {
      toast.error("Please enter an expiry date (e.g. 04 MAR 2026)", {
        position: "top-left",
        autoClose: 3000,
        theme: "dark",
      });
      return;
    }

    try {
      setSubmittingRow(rowNumber);

      // 1. Update the Google Sheet
      const updatedRows = await InsuranceSyncService.handleExpirySubmit(
        rowNumber,
        expiryDate,
      );

      // 2. Send automated messages (fire-and-forget, don't block UI)
      try {
        const messagePromises = [];

        // SMS to customer phone
        if (row?.customerDetails) {
          messagePromises.push(
            FixedAgent.sendSms({
              recipients: row.customerDetails,
              message: `Hello! Your insurance for vehicle ${row.plateNumber} has been processed. Expiry date: ${expiryDate}. Thank you for choosing AgentPro.`,
            })
              .then((res) => {
                console.log("SMS response:", res);
                return res;
              })
              .catch((err) => console.error("SMS to customer failed:", err)),
          );
        }

        // WhatsApp to agent phone
        if (row?.agentDetails) {
          messagePromises.push(
            FixedAgent.sendWhatsAppMessageToNonUser({
              token,
              phone_no: row.agentDetails,
              message: `The vehicle   ${row.make} ${row.model} registration number ${row.plateNumber} has been successfully uploaded to askNIID.Thanks for trusting Agent Pro.`,
            })
              .then((res) => {
                console.log("WhatsApp response:", res);
                return res;
              })
              .catch((err) => console.error("WhatsApp to agent failed:", err)),
          );
        }

        await Promise.allSettled(messagePromises);
      } catch (msgErr) {
        console.error("Messaging error (non-blocking):", msgErr);
      }

      setPendingRows(updatedRows);

      // Clear the expiry date input for the submitted row
      setExpiryDates((prev) => {
        const updated = { ...prev };
        delete updated[rowNumber];
        return updated;
      });

      toast.success("Entry updated & messages sent!", {
        position: "top-left",
        autoClose: 3000,
        theme: "dark",
      });

      if (updatedRows.length === 0) {
        setSyncMessage("All entries have been processed.");
      } else {
        setSyncMessage(
          `${updatedRows.length} pending ${updatedRows.length === 1 ? "entry" : "entries"} remaining.`,
        );
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.message || "Failed to update entry", {
        position: "top-left",
        autoClose: 5000,
        theme: "dark",
      });
    } finally {
      setSubmittingRow(null);
    }
  };

  /**
   * Manual refresh
   */
  const handleRefresh = async () => {
    setSyncing(true);
    await loadData();
    setSyncing(false);
  };

  return (
    <div>
      <ToastContainer />
      <div className="menu" onClick={closeMenuHandler}>
        {props.showMenu && <Menu setShowMenu={props.setShowMenu} />}
      </div>
      <Navbar title="Insurance Sync" setShowMenu={props.setShowMenu} />
      <div className={styles.body}>
        <div className={styles.body_inner}>
          <h1 className={styles.page_title}>Insurance Data Sync</h1>
          <p className={styles.page_subtitle}>
            Manage pending insurance entries and set expiry dates.
          </p>
        </div>

        {/* Sync status banner */}
        {syncMessage && !loading && (
          <div
            className={`${styles.sync_banner} ${
              pendingRows.length === 0
                ? styles.sync_banner_success
                : styles.sync_banner_info
            }`}
          >
            <span>{pendingRows.length === 0 ? "✓" : "ℹ"}</span>
            <span>{syncMessage}</span>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className={styles.loading_container}>
            <div className={styles.loading_spinner}></div>
            <p className={styles.loading_text}>Syncing insurance data...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && pendingRows.length === 0 && (
          <div className={styles.empty_state}>
            <span className={styles.empty_icon}>✅</span>
            <h3 className={styles.empty_title}>All Caught Up!</h3>
            <p className={styles.empty_text}>
              No pending insurance entries to process.
            </p>
          </div>
        )}

        {/* Search + Date filter controls */}
        {!loading && pendingRows.length > 0 && (
          <div className={styles.filters_row}>
            <div className={styles.search_wrapper}>
              <svg
                className={styles.search_icon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                className={styles.search_input}
                placeholder="Search plate number..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <input
              type="date"
              className={styles.date_filter_input}
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setCurrentPage(1);
              }}
              title="Filter by date"
            />
            {hasActiveFilters && (
              <button
                className={styles.clear_filters_btn}
                onClick={clearFilters}
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Entries list */}
        {!loading && pendingRows.length > 0 && (
          <>
            <p className={styles.entries_count}>
              <span>{filteredRows.length}</span>
              {hasActiveFilters ? " matching" : " pending"}{" "}
              {filteredRows.length === 1 ? "entry" : "entries"}
              {filteredRows.length > 0 && (
                <>
                  {" · Page "}
                  {currentPage}
                  {" of "}
                  {clientPage.pagination.lastPage}
                </>
              )}
            </p>
            {filteredRows.length === 0 && hasActiveFilters && (
              <div className={styles.empty_state}>
                <span className={styles.empty_icon}>🔍</span>
                <h3 className={styles.empty_title}>No Results</h3>
                <p className={styles.empty_text}>
                  No entries match your search or date filter.
                </p>
              </div>
            )}
            <div className={styles.entries_list}>
              {clientPage.items.map((row) => (
                  <div key={row.rowNumber} className={styles.entry_card}>
                    <div className={styles.entry_header}>
                      <div className={styles.plate_copy_wrapper}>
                        <p className={styles.plate_number}>{row.plateNumber}</p>
                        <button
                          className={styles.copy_btn}
                          onClick={() =>
                            handleCopyPlate(row.plateNumber, row.rowNumber)
                          }
                          title="Copy plate number"
                        >
                          {copiedRow === row.rowNumber ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                fill="#10B981"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                                fill="#9CA3AF"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                      <span
                        className={`${styles.status_badge} ${styles.status_pending}`}
                      >
                        Pending
                      </span>
                    </div>

                    <div className={styles.entry_details}>
                      <div className={styles.entry_detail_row}>
                        <p className={styles.detail_label}>Insurance Type</p>
                        <p className={styles.detail_value}>
                          {row.insuranceType}
                        </p>
                      </div>
                      <div className={styles.entry_detail_row}>
                        <p className={styles.detail_label}>Make</p>
                        <p className={styles.detail_value}>
                          {row.make}
                        </p>
                      </div>
                      <div className={styles.entry_detail_row}>
                        <p className={styles.detail_label}>Model</p>
                        <p className={styles.detail_value}>
                          {row.model}
                        </p>
                      </div>
                      {/* <div className={styles.entry_detail_row}>
                         <p className={styles.detail_label}>Agent Phone</p>
                         <p className={styles.detail_value}>
                           {row.agentDetails}
                         </p>
                       </div> */}
                      {/* <div className={styles.entry_detail_row}>
                         <p className={styles.detail_label}>Customer Phone</p>
                         <p className={styles.detail_value}>
                           {row.customerDetails}
                         </p>
                       </div> */}
                      {/* <div className={styles.entry_detail_row}>
                       <p className={styles.detail_label}>Timestamp</p>
                       <p className={styles.detail_value}>{row.timestamp}</p>
                     </div> */}
                    </div>

                    <div className={styles.expiry_form}>
                      <input
                        type="text"
                        className={styles.expiry_input}
                        value={expiryDates[row.rowNumber] || ""}
                        onChange={(e) =>
                          handleExpiryChange(
                            row.rowNumber,
                            e.target.value.toUpperCase(),
                          )
                        }
                        disabled={submittingRow === row.rowNumber}
                        placeholder="e.g. 04 MAR 2026"
                      />
                      <button
                        className={styles.submit_btn}
                        onClick={() => handleSubmit(row.rowNumber)}
                        disabled={
                          submittingRow === row.rowNumber ||
                          !expiryDates[row.rowNumber]
                        }
                      >
                        {submittingRow === row.rowNumber
                          ? "Saving..."
                          : "Submit"}
                      </button>
                    </div>
                  </div>
                ))}
            </div>

            <Pagination
              pagination={clientPage.pagination}
              onPageChange={setCurrentPage}
              itemLabel="pending entries"
            />
          </>
        )}

        {/* Refresh button */}
        {!loading && (
          <button
            className={styles.refresh_btn}
            onClick={handleRefresh}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "↻ Refresh Data"}
          </button>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default InsuranceSync;
